import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { profileSelector } from 'selectors'
import { updateProfile } from 'actions/profile'
import { Helmet } from 'react-helmet'
import styled from 'styled-components/macro'
import Page from 'rui/widgets/Page'
import Thin from 'components/layout/Thin'
import Panel, { PanelHeading, PanelSubheading } from 'components/layout/Panel'
import Form from 'components/form'
import Input from 'components/form/Input'
import { ssn, exactly } from 'components/form/validations'
import Step from 'components/misc/Step'
import Icon from 'components/misc/Icon'
import Spacer from 'components/misc/Spacer'
import { formatRegistrationStep } from 'util/registrationFlow'
import { useLocalization, LocalizedText } from 'localization'

const IconList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  justify-content: space-between;
  padding-top: 0.8rem;

  li {
    margin: 0;
    list-style: none;
    font-size: 1.1rem;
    font-weight: 700;
    position: relative;
    color: ${(props) => props.theme.colors.greyDark};
    text-transform: uppercase;
    display: flex;
    align-items: center;
  }
`

const StyledIcon = styled(Icon).attrs(() => ({ palette: 'grey', shade: 'light' }))`
  width: 1.4rem;
  height: 1.4rem;
  margin-right: 0.5rem;
`

// Social Security Number collection. Drivers enter their SSN twice to guard
// against typos. The confirm field stays disabled until the first field has a value and
// must match it. On submit, the SSN is sent via a single updateProfile call.
const SsnStep = () => {
  const { t } = useLocalization()
  const dispatch = useDispatch()
  const [ssnValue, setSsnValue] = useState('')
  const { profile } = useSelector(profileSelector)
  const registrationStep = formatRegistrationStep('SSN', profile)
  const handleData = (data) => dispatch(updateProfile({ profile: { ssn: data.ssn } }))
  // Use withMessage (not mutating .message) since exactly() is memoized and the instance
  // is shared across callers — see components/form/validations.js.
  const mustMatch = exactly(ssnValue).withMessage(t('dweb_validation_social_security_numbers_do_not_match'))

  return (
    <Page>
      <Helmet>
        <title>{t('dweb_ssn_step_social_security')}</title>
      </Helmet>
      <Thin>
        <Form onData={handleData}>
          <Panel>
            <PanelHeading>
              <LocalizedText translationKey="dweb_ssn_step_social_security" />
            </PanelHeading>
            <PanelSubheading>
              <LocalizedText translationKey="dweb_background_safety_our_community_roadie_orders_consumer_reports" />
            </PanelSubheading>

            <Input.Integer
              password
              maxLength={9}
              className="fs-block"
              type="tel"
              label={t('dweb_background_social_security_number')}
              name="ssn"
              validate={ssn.required()}
              onChange={(event) => setSsnValue(event.target.value.replace(/\D/g, ''))}
            />

            <Input.Integer
              password
              maxLength={9}
              className="fs-block"
              type="tel"
              label={t('dweb_ssn_step_confirm_social_security_number')}
              name="ssn_confirmation"
              disabled={!ssnValue}
              validate={mustMatch.required()}
            />

            <IconList>
              <li>
                <StyledIcon symbol="lock" /> <LocalizedText translationKey="dweb_common_secure_transfer" />
              </li>
              <li>
                <StyledIcon symbol="shield" /> <LocalizedText translationKey="dweb_common_never_shared" />
              </li>
              <li>
                <StyledIcon symbol="ban" /> <LocalizedText translationKey="dweb_common_no_credit_checks" />
              </li>
            </IconList>

            <Input.Submit value={t('dweb_common_next')} />

            <Spacer height="1.6rem" />

            {registrationStep && <Step>{registrationStep}</Step>}
          </Panel>
        </Form>
      </Thin>
    </Page>
  )
}

export default SsnStep
